import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Chip,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

const SelecaoEmpresa: React.FC = () => {
  const { user, tenants, selectTenant } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!user) {
    return <Navigate to="/tw/login" replace />;
  }

  const handleSelectTenant = async (tenantId: string) => {
    setError(null);
    setLoading(tenantId);
    try {
      await selectTenant(tenantId);
      navigate('/tw/dashboard');
    } catch (err: any) {
      setError(err.message || 'Erro ao selecionar empresa');
    } finally {
      setLoading(null);
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url("/landing-page-background.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            backgroundColor: '#1F2C40',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src="/logo192.png"
            alt="ThinkWay"
            sx={{ width: 100, height: 'auto', mb: 2, filter: 'brightness(1.2)' }}
          />

          <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
            Selecione uma empresa
          </Typography>

          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3, textAlign: 'center' }}>
            Olá, {user.name}! Você possui acesso a múltiplas empresas.
          </Typography>

          {error && (
            <Typography sx={{ color: '#ff6b6b', mb: 2, fontSize: '0.875rem', textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {tenants.map((tenant) => (
              <Card
                key={tenant.tenantId}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleSelectTenant(tenant.tenantId)}
                  disabled={loading !== null}
                  sx={{ p: 1 }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BusinessIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 32 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '1rem' }}>
                        {tenant.empresaNome}
                      </Typography>
                      <Chip
                        label={tenant.role}
                        size="small"
                        sx={{
                          mt: 0.5,
                          backgroundColor: 'rgba(255,255,255,0.12)',
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: '0.7rem',
                          height: 22,
                        }}
                      />
                    </Box>
                    {loading === tenant.tenantId && (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>
      </Container>
    </Container>
  );
};

export default SelecaoEmpresa;
